import { createContext, useContext, useState, useEffect } from "react";
import { LocalStorageService } from "../auth/LocalStorageService";
import { CommissionRule, Permission, User } from "../../constants/types";



type GlobalStateType = {
  permission: Permission[]; // Fix: Use Permission[] instead of string[]
  setPermission: (value: Permission[]) => void;
  token: string | null;
  setToken: (value: string) => void;
  userInfo: User | null;
  setUserInfo: (value: User) => void;
  commissionRules: CommissionRule[];
  setCommissionRules: (value: CommissionRule[]) => void;
};

const GlobalContext = createContext<GlobalStateType | undefined>(undefined);

export const GlobalProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [userInfo, setUserInfo] = useState<User | null>(() => {
    // Load userInfo from localStorage when initializing state
    const storedUserInfo = localStorage.getItem("userinfo");
    return storedUserInfo ? JSON.parse(storedUserInfo) : null;
  });
  const [permission, setPermission] = useState<Permission[]>(() => {
    // Load permissions from localStorage when initializing state
    const storedPermissions = localStorage.getItem("permissions");
    const value =
      storedPermissions !== "undefined" && storedPermissions !== null
        ? JSON.parse(storedPermissions ?? "")
        : [];
    return value;
  });

  const [token, setToken] = useState<string | null>(null);
  const fetchToken = async () => {
    const storedToken = await LocalStorageService.getToken();
    setToken(storedToken);
  };

  useEffect(() => {
    fetchToken();
  }, []);

  // Save permissions to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("permissions", JSON.stringify(permission));
  }, [permission]);

  useEffect(() => {
    localStorage.setItem("userinfo", JSON.stringify(userInfo));
  }, [userInfo]);

  const [commissionRules, setCommissionRules] = useState<CommissionRule[]>(
    () => {
      try {
        const storedRules = localStorage.getItem("commissionRules");
        return storedRules ? JSON.parse(storedRules) : [];
      } catch (e) {
        console.error("Failed to parse commission rules:", e);
        return [];
      }
    }
  );
  
  useEffect(() => {
    localStorage.setItem("commissionRules", JSON.stringify(commissionRules));
  }, [commissionRules]);

  return (
    <GlobalContext.Provider
      value={{
        permission,
        setPermission,
        token,
        setToken,
        setUserInfo,
        userInfo,
        commissionRules,
        setCommissionRules,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobalState = () => {
  const context = useContext(GlobalContext);
  if (!context)
    throw new Error("useGlobalState must be used within a GlobalProvider");
  return context;
};
